import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { fetchNationalStats, ISSUE_TAGS } from "../services/citizenAgendaService";
import "../App.css";

interface TopMunicipality {
  id: string;
  name: string;
  province: string;
  vote_count: number;
}

interface TopIssue {
  tag_id: string;
  label: string;
  icon: string;
  vote_count: number;
}

interface Comment {
  custom_comment: string;
  tambon_name: string;
  province: string;
  issue_tags: string[];
  issue_labels: string[];
  created_at: string;
}

interface AnalyticsData {
  total_votes: number;
  total_municipalities: number;
  top_issues: TopIssue[];
  top_municipalities: TopMunicipality[];
  recent_comments: Comment[];
}

const COLORS = [
  "#00d2ca",
  "#00b8b0",
  "#5c48f6",
  "#ff5062",
  "#ffc658",
  "#82ca9d",
  "#8884d8",
  "#8dd1e1",
  "#a4de6c",
  "#d0ed57",
];

const AnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const stats = await fetchNationalStats();
        if (stats) {
          const formattedTopIssues = (stats.top_issues || []).map((issue) => {
            const tagInfo = ISSUE_TAGS.find((t) => t.id === issue.tag_id);
            return {
              ...issue,
              label: tagInfo?.label || issue.label || issue.tag_id,
            };
          });
          setData({
            total_votes: stats.total_votes || 0,
            total_municipalities: stats.total_municipalities || 0,
            top_issues: formattedTopIssues,
            top_municipalities: stats.top_municipalities || [],
            recent_comments: stats.recent_comments || [],
          });
        } else {
          setError("ไม่สามารถโหลดข้อมูลได้");
        }
      } catch (err) {
        console.error("Error loading analytics:", err);
        setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleDownload = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    window.open(`${apiUrl}/api/citizen-agenda/export`, "_blank");
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">
          <div className="loading-spinner"></div>
          <span>กำลังโหลดข้อมูล...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="analytics-page">
        <div className="analytics-error">
          <p>{error || "ไม่สามารถโหลดข้อมูลได้"}</p>
        </div>
      </div>
    );
  }

  const topIssue = data.top_issues.length > 0 ? data.top_issues[0] : null;

  return (
    <div className="analytics-page">
      <div className="analytics-hero">
        <div className="analytics-hero-content">
          <h1 className="analytics-title">Dashboard วิเคราะห์ข้อมูล Citizen Agenda</h1>
          <p className="analytics-subtitle">ข้อมูลสรุปประเด็นความคิดเห็นจากประชาชนทั่วประเทศ</p>
          <button className="download-btn" onClick={handleDownload}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            ดาวน์โหลดข้อมูล CSV
          </button>
        </div>
      </div>

      <div className="analytics-content">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon stat-icon-blue">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">จำนวนโหวตทั้งหมด</span>
              <span className="stat-value">{data.total_votes.toLocaleString()}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">อบต./เทศบาล ที่เข้าร่วม</span>
              <span className="stat-value">{data.total_municipalities.toLocaleString()}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon-orange">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                <polyline points="17 6 23 6 23 12"></polyline>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">ประเด็นยอดนิยมอันดับ 1</span>
              <span className="stat-value">{topIssue?.label || "-"}</span>
              {topIssue && <span className="stat-sub">{topIssue.vote_count.toLocaleString()} โหวต</span>}
            </div>
          </div>
        </div>

        <div className="analytics-grid">
          <div className="analytics-card">
            <h3 className="card-title">สถิติประเด็นความต้องการ</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={data.top_issues}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e9ecef" />
                  <XAxis type="number" tick={{ fill: '#2b2b3d', fontSize: 12 }} />
                  <YAxis 
                    dataKey="label" 
                    type="category" 
                    width={90} 
                    tick={{ fill: '#2b2b3d', fontSize: 14, fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    formatter={(value: number) => [`${value.toLocaleString()} โหวต`, 'จำนวน']}
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      fontFamily: 'Prompt, sans-serif'
                    }}
                  />
                  <Bar dataKey="vote_count" name="จำนวนโหวต" radius={[0, 6, 6, 0]}>
                    {data.top_issues.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="analytics-card">
            <h3 className="card-title">10 อันดับพื้นที่ที่มีการมีส่วนร่วมสูงสุด</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>อันดับ</th>
                    <th>พื้นที่</th>
                    <th>จังหวัด</th>
                    <th>จำนวนโหวต</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_municipalities.map((muni, index) => (
                    <tr key={muni.id || index}>
                      <td className="rank-cell">{index + 1}</td>
                      <td className="name-cell">{muni.name}</td>
                      <td className="province-cell">{muni.province}</td>
                      <td className="vote-cell">{muni.vote_count.toLocaleString()}</td>
                    </tr>
                  ))}
                  {data.top_municipalities.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: '#adb5bd' }}>
                        ยังไม่มีข้อมูล
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="comments-section">
          <div className="analytics-card comments-card">
            <h3 className="card-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px', verticalAlign: 'middle' }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              ความคิดเห็นจากประชาชน ({data.recent_comments.length} รายการล่าสุด)
            </h3>
            <div className="comments-container">
              {data.recent_comments.length > 0 ? (
                data.recent_comments.map((comment, index) => (
                  <div key={index} className="comment-card">
                    <div className="comment-header">
                      <div className="comment-location">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span>{comment.tambon_name}, {comment.province}</span>
                      </div>
                      <div className="comment-date">
                        {comment.created_at ? new Date(comment.created_at).toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : '-'}
                      </div>
                    </div>
                    <div className="comment-content">
                      "{comment.custom_comment}"
                    </div>
                    <div className="comment-tags">
                      {(comment.issue_labels || []).map((label, tagIndex) => (
                        <span key={tagIndex} className="comment-tag">{label}</span>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-comments">
                  <p>ยังไม่มีความคิดเห็น</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
